import React from "react";
import { formatLocalDateTime } from "../../utils/dateTime";
import { intervalLabel } from "./tagCreation";

const NOT_AVAILABLE = "Not available";

function fieldText(field) {
    if (!field || field.available !== true) {
        return NOT_AVAILABLE;
    }
    const value = field.value;
    if (typeof value === "boolean") {
        return value ? "True" : "False";
    }
    if (value == null || value === "") {
        return NOT_AVAILABLE;
    }
    if (Array.isArray(value)) {
        return value.length ? value.join(", ") : NOT_AVAILABLE;
    }
    return String(value);
}

function timeText(field) {
    if (!field || field.available !== true || !field.value) {
        return NOT_AVAILABLE;
    }
    const formatted = formatLocalDateTime(field.value);
    if (!formatted || formatted === "N/A" || formatted === "Invalid time") {
        return NOT_AVAILABLE;
    }
    return formatted;
}

function rangeText(field) {
    if (!field || field.available !== true || !field.value) {
        return NOT_AVAILABLE;
    }
    const low = field.value.low;
    const high = field.value.high;
    if (low == null && high == null) {
        return NOT_AVAILABLE;
    }
    return `${low == null ? NOT_AVAILABLE : low} to ${high == null ? NOT_AVAILABLE : high}`;
}

function unitsText(field) {
    if (!field || field.available !== true || !field.value) {
        return NOT_AVAILABLE;
    }
    const parts = [field.value.display_name, field.value.description].filter(Boolean);
    if (field.value.unit_id != null) {
        parts.push(`unit id ${field.value.unit_id}`);
    }
    return parts.length ? parts.join(", ") : NOT_AVAILABLE;
}

function qualityClass(field) {
    const name = field && field.available === true ? String(field.value || "") : "";
    if (name.startsWith("Good")) {
        return "is-good";
    }
    if (name.startsWith("Bad")) {
        return "is-bad";
    }
    if (name.startsWith("Uncertain")) {
        return "is-uncertain";
    }
    return "";
}

function Fact({ label, children }) {
    return (
        <div>
            <dt>{label}</dt>
            <dd>{children}</dd>
        </div>
    );
}

export default function OpcNodeDetails({ details, loading, error, listing }) {
    if (loading) {
        return <p className="tag-page__empty">Reading node metadata…</p>;
    }
    if (error) {
        return <p className="tag-page__banner is-error" role="alert">{error}</p>;
    }
    if (!details) {
        return <p className="tag-page__empty">OPC metadata has not been read for this node.</p>;
    }

    const identity = details.identity || {};
    const dataType = details.data_type || {};
    const access = details.access || {};
    const value = details.value || {};
    const sampling = details.sampling || {};
    const engineering = details.engineering || {};
    const suggestion = details.suggestion || {};

    return (
        <div className="tag-page__metadata">
            <section aria-labelledby="opc-meta-listing">
                <h4 id="opc-meta-listing">Browse listing</h4>
                <dl className="tag-page__facts">
                    <Fact label="Parent">{listing?.parents || "Root"}</Fact>
                    <Fact label="Browse type">{listing?.type || NOT_AVAILABLE}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-identity">
                <h4 id="opc-meta-identity">Identity</h4>
                <p className="tag-page__meta-source">OPC server metadata</p>
                <dl className="tag-page__facts">
                    <Fact label="Node ID">{fieldText(identity.node_id)}</Fact>
                    <Fact label="Browse name">{fieldText(identity.browse_name)}</Fact>
                    <Fact label="Display name">{fieldText(identity.display_name)}</Fact>
                    <Fact label="Description">{fieldText(identity.description)}</Fact>
                    <Fact label="Node class">{fieldText(identity.node_class)}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-type">
                <h4 id="opc-meta-type">Data type and structure</h4>
                <dl className="tag-page__facts">
                    <Fact label="Data type Node ID">{fieldText(dataType.data_type_node_id)}</Fact>
                    <Fact label="Data type">{fieldText(dataType.data_type_name)}</Fact>
                    <Fact label="Variant type">{fieldText(dataType.variant_type)}</Fact>
                    <Fact label="Value rank">{fieldText(dataType.value_rank_label)}</Fact>
                    <Fact label="Array dimensions">{fieldText(dataType.array_dimensions)}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-access">
                <h4 id="opc-meta-access">Read and write capabilities</h4>
                <dl className="tag-page__facts">
                    <Fact label="Access level">{fieldText(access.access_level_flags)}</Fact>
                    <Fact label="User access level">{fieldText(access.user_access_level_flags)}</Fact>
                    <Fact label="Server advertised CurrentWrite">{fieldText(access.server_current_write)}</Fact>
                    <Fact label="This OPC session CurrentWrite">{fieldText(access.session_current_write)}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-value">
                <h4 id="opc-meta-value">Current value and quality</h4>
                <dl className="tag-page__facts">
                    <Fact label="Current value">{fieldText(value.current_value)}</Fact>
                    <Fact label="OPC status">
                        <span className={`tag-page__quality ${qualityClass(value.quality)}`}>{fieldText(value.quality)}</span>
                    </Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-time">
                <h4 id="opc-meta-time">Timestamps</h4>
                <dl className="tag-page__facts">
                    <Fact label="Source timestamp">{timeText(value.source_timestamp)}</Fact>
                    <Fact label="Server timestamp">{timeText(value.server_timestamp)}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-sampling">
                <h4 id="opc-meta-sampling">Sampling and history</h4>
                <dl className="tag-page__facts">
                    <Fact label="Minimum sampling interval">
                        {sampling.minimum_sampling_interval_ms?.available === true
                            ? `${fieldText(sampling.minimum_sampling_interval_ms)} ms`
                            : NOT_AVAILABLE}
                    </Fact>
                    <Fact label="Historizing">{fieldText(sampling.historizing)}</Fact>
                </dl>
            </section>

            <section aria-labelledby="opc-meta-engineering">
                <h4 id="opc-meta-engineering">Engineering metadata</h4>
                <dl className="tag-page__facts">
                    <Fact label="Engineering units">{unitsText(engineering.engineering_units)}</Fact>
                    <Fact label="EU range">{rangeText(engineering.eu_range)}</Fact>
                    <Fact label="Instrument range">{rangeText(engineering.instrument_range)}</Fact>
                </dl>
            </section>

            <section className="tag-page__suggestion" aria-labelledby="opc-meta-suggestion">
                <h4 id="opc-meta-suggestion">Telemindra recommendation</h4>
                <p>Inferred from the metadata above. This is not an OPC attribute and it is not saved by browsing.</p>
                <dl className="tag-page__facts">
                    <Fact label="Suggested signal type">{suggestion.signal_type || "Manual choice"}</Fact>
                    <Fact label="Recording mode">{suggestion.recording_mode === "ON_CHANGE" ? "On Change" : "Always (Monitoring)"}</Fact>
                    <Fact label="On Change Interval">{intervalLabel(suggestion.on_change_interval_seconds || ((suggestion.on_change_interval_minutes || 5) * 60))}</Fact>
                    <Fact label="Decimal places">{suggestion.decimal_places == null ? "Not applicable" : suggestion.decimal_places}</Fact>
                    <Fact label="Suggested access mode">Read only</Fact>
                    <Fact label="Storage">{suggestion.storage_support || NOT_AVAILABLE}</Fact>
                    <Fact label="OPC engineering unit">{suggestion.engineering_units || NOT_AVAILABLE}</Fact>
                </dl>
                {Array.isArray(suggestion.notes) && suggestion.notes.length > 0 && (
                    <ul>
                        {suggestion.notes.map((note) => <li key={note}>{note}</li>)}
                    </ul>
                )}
            </section>
        </div>
    );
}
